import './header.scss';

import { kebabCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { NavLink, useMatch } from 'react-router-dom';

import { ProfileModal } from '../../compositions/profile/modals/profile-modal/profile-modal';
import { useAppContext } from '../../data/context/app-context';
import { HealthProviderItemDataFragment } from '../../data/types/api/graphql';
import { useNavigateWithHealthProvider } from '../../data/utils/router.utils';
import {
  ContextMenuHorizontalPosition,
  ContextMenuVerticalPosition,
  useContextMenu,
} from '../../data/utils/use-context-menu';
import { Button, ButtonType } from '../button/button';
import { ContextMenu, ContextMenuItemIcon } from '../context-menu/context-menu';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';
import { Version } from '../utils/version/version';

export function Header() {
  const {
    signOut,
    lock,
    lockAll,
    user,
    healthProviderId,
    healthProviders,
    updateHealthProviderId,
    connection,
  } = useAppContext();

  const [profileModalOpen, setProfileModalOpen] = useState<boolean>(false);

  const isStartPage = useMatch('/:wildcard');

  const menuItems = useMemo(() => {
    const items = [
      'agenda',
      'conversations',
      'reminders',
      'bulk-messages',
      'patients',
      'schedule',
    ];

    if (user?.isAdmin) {
      items.push('admin');
    }

    return items.map(option => ({
      title: option,
      active: option === 'conversations' && isStartPage,
    }));
  }, [isStartPage, user?.isAdmin]);

  const navigate = useNavigateWithHealthProvider();

  const onHandleHealthProvider = useCallback(
    (value: HealthProviderItemDataFragment) => {
      if (value.id) {
        updateHealthProviderId(value.id);
      }
    },
    [updateHealthProviderId]
  );

  const onHandleHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const {
    contextMenuState: accountContextMenuState,
    onOpenContextMenu: onOpenAccountContextMenu,
  } = useContextMenu({
    horizontal: ContextMenuHorizontalPosition.Right,
    vertical: ContextMenuVerticalPosition.Bottom,
  });

  return (
    <>
      <header className="header">
        <div className="header__top">
          <button type="button" className="logo" onClick={onHandleHome}>
            <img src="/interface/img/doccs-logo-white.svg" alt="doccs" />
          </button>
          <nav>
            <ul>
              {healthProviders.map((option: HealthProviderItemDataFragment) => (
                <li key={option.id}>
                  <button
                    type="button"
                    title={option.name ?? ''}
                    className={healthProviderId === option.id ? 'active' : ''}
                    onClick={() => onHandleHealthProvider(option)}
                  >
                    {option.name}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <Version />

          {user && (
            <Button
              type={ButtonType.SECONDARY_WHITE}
              inverted
              toggle
              onClick={onOpenAccountContextMenu}
            >
              <div>
                <span
                  title={
                    connection !== null
                      ? 'Realtime updates connected'
                      : 'Realtime updates disconnected'
                  }
                  className={`header__connection${
                    connection !== null ? ' header__connection--connected' : ''
                  }`}
                />
                {user.initials ?? ''}
              </div>
            </Button>
          )}
        </div>
        <div className="header__bottom">
          <nav>
            <ul>
              {menuItems.map(option => (
                <li key={option.title}>
                  <NavLink
                    data-test-id={`header.navigation.${option.title}`}
                    className={({ isActive }) =>
                      isActive || option.active ? `active` : ``
                    }
                    to={`/${healthProviderId}/${option.title}`}
                  >
                    <CustomFormattedMessage
                      id={`header.${kebabCase(option.title)}`}
                    />
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      {accountContextMenuState && (
        <ContextMenu
          testId="profile"
          state={accountContextMenuState}
          items={[
            {
              key: 'profile',
              icon: ContextMenuItemIcon.User,
              title: <CustomFormattedMessage id="profile.title" />,
              onClick: () => setProfileModalOpen(true),
            },
            {
              key: 'pincode',
              icon: ContextMenuItemIcon.Password,
              title: <CustomFormattedMessage id="common.lock" />,
              onClick: () => {
                lock();
              },
            },
            {
              key: 'pincode-all',
              icon: ContextMenuItemIcon.Lock,
              title: <CustomFormattedMessage id="common.lock-all" />,
              onClick: () => {
                lockAll();
              },
            },
            {
              key: 'logout',
              icon: ContextMenuItemIcon.Logout,
              title: <CustomFormattedMessage id="common.logout" />,
              onClick: () => {
                signOut();
              },
            },
          ]}
        />
      )}

      {profileModalOpen && (
        <ProfileModal onClose={() => setProfileModalOpen(false)} />
      )}
    </>
  );
}
